import { parseCardSourceRefCached } from '../../deck/sources';
import { studyStatesOf } from './card-utils';
import { adjustedDayKey, randomNumberForIdOnDay } from './fuzz';
import type { SpacedRepetitionCard, SpacedRepetitionConfig } from './types';

export function createDetermisticCardShuffler<CardType extends SpacedRepetitionCard>(
  date: Date,
  cutoffTime: SpacedRepetitionConfig['cutoffTime'],
  seed: number = 0,
) {
  // Same values as `getRandomNumberForId(id, date, cutoffTime, seed)`, but the day
  // is resolved once and each id hashed once per shuffler — a sort compares every
  // card ~log2(n) times, and each hash is a sha256. Fast Review re-sorts the whole
  // remaining queue on every answer; uncached, a 1000-card shuffled deck stalled
  // each swipe on Hermes.
  const dayKey = adjustedDayKey(date, cutoffTime);
  const byId = new Map<string, number>();
  const valueFor = (id: string) => {
    let v = byId.get(id);
    if (v === undefined) {
      v = randomNumberForIdOnDay(id, dayKey, seed);
      byId.set(id, v);
    }
    return v;
  };
  return (card1: CardType, card2: CardType) => valueFor(card1.id) - valueFor(card2.id);
}

/**
 * `compareAsc` from date-fns for ISO strings (an unreadable date compares as a tie,
 * as there), minus its wrapper allocations — these run ~n·log2(n) times per sort.
 */
function compareIso(a: string | Date, b: string | Date): number {
  const diff = new Date(a).getTime() - new Date(b).getTime();
  return diff < 0 ? -1 : diff > 0 ? 1 : 0;
}

/**
 * Byte-order sortKey comparison (NOT localeCompare): fractional-indexing
 * keys are base-62 and rely on ASCII ordering where uppercase < lowercase.
 * Kept local — importing the sync-client sort-key util would drag the
 * ESM-only `fractional-indexing` package into every algorithms jest spec.
 */
function compareSortKeyBytes(a: string | undefined, b: string | undefined): number {
  const ak = a ?? '';
  const bk = b ?? '';
  if (ak < bk) return -1;
  if (ak > bk) return 1;
  return 0;
}

/** Deterministic last-resort tiebreak so no sorter ever falls through to
 *  input order — the input is the sync store's map order, which is
 *  cardId-lexicographic (effectively random) after any reload/reconnect. */
function tieBreakBySortKeyThenId(
  card1: SpacedRepetitionCard,
  card2: SpacedRepetitionCard,
) {
  return (
    compareSortKeyBytes(card1.sortKey, card2.sortKey) ||
    (card1.id < card2.id ? -1 : card1.id > card2.id ? 1 : 0)
  );
}

/**
 * The deck's canonical order — the same sortKey order the flashcards view
 * renders (and Anki export writes). Cards without a sortKey (legacy, not
 * yet backfilled) fall back to creation date, then id.
 */
export function cardSorterBySortKey(
  card1: SpacedRepetitionCard,
  card2: SpacedRepetitionCard,
) {
  return (
    compareSortKeyBytes(card1.sortKey, card2.sortKey) ||
    compareIso(card1.createdTimeISO, card2.createdTimeISO) ||
    (card1.id < card2.id ? -1 : card1.id > card2.id ? 1 : 0)
  );
}

export function cardSorterByDueDate(
  card1: SpacedRepetitionCard,
  card2: SpacedRepetitionCard,
) {
  const cardStudyState1 = studyStatesOf(card1);
  const cardStudyState2 = studyStatesOf(card2);

  if (cardStudyState1.length === 0 && cardStudyState2.length === 0) {
    return cardSortedByCreationDate(card1, card2);
  }

  if (cardStudyState1.length === 0) {
    return 1;
  }
  if (cardStudyState2.length === 0) {
    return -1;
  }

  return (
    compareIso(
      cardStudyState1[cardStudyState1.length - 1].algoInfo.nextDueISO,
      cardStudyState2[cardStudyState2.length - 1].algoInfo.nextDueISO,
    ) || cardSortedByCreationDate(card1, card2)
  );
}

export function cardSortedByCreationDate(
  card1: SpacedRepetitionCard,
  card2: SpacedRepetitionCard,
) {
  // AI/Notes batches stamp createdTimeISO per card in a tight loop, so a
  // whole batch ties at the same millisecond — without a tiebreak the
  // stable sort collapses ties into map order (random after a reload).
  return (
    compareIso(card1.createdTimeISO, card2.createdTimeISO) ||
    tieBreakBySortKeyThenId(card1, card2)
  );
}

/**
 * Where a card sits in the set, document by document: which document it is
 * anchored in, then where in that document.
 *
 * Offsets of two different files mean nothing to each other, so cards are
 * never interleaved across documents — the first document is read to its
 * end before the next one starts. This orders ONE deck's cards; a folder's
 * pool goes through {@link deckByDeck}. `sourceIds` is the set's document order
 * (`SyncDeckMeta.sources`, primary first); a bare ref belongs to the primary.
 * Without it the primary still comes first and the other documents follow in
 * a stable (id) order. A document no longer in the set keeps its cards
 * together after the set's documents.
 *
 * Within a document: text-anchored cards by offset, then image regions by
 * page, top-to-bottom, left-to-right (interleaving those with text needs a
 * page→offset map this sorter does not have). Then every card with no
 * position (manual, notes, legacy chunk refs) by creation date. Every tie
 * falls to sortKey, then id — never to input order.
 */
export function cardSorterByDocumentPosition(sourceIds?: readonly string[]) {
  const primaryId = sourceIds?.[0];
  const known = sourceIds?.length ?? 1;
  const documentOf = (sourceId: string | undefined): { rank: number; key: string } => {
    if (sourceId === undefined || sourceId === primaryId) return { rank: 0, key: '' };
    const index = sourceIds ? sourceIds.indexOf(sourceId) : -1;
    if (index >= 0) return { rank: index, key: '' };
    return { rank: known, key: sourceId };
  };

  return (card1: SpacedRepetitionCard, card2: SpacedRepetitionCard): number => {
    const refA = parseCardSourceRefCached(card1.source);
    const refB = parseCardSourceRefCached(card2.source);
    const placedA = refA.type === 'text-range' || refA.type === 'page-rect';
    const placedB = refB.type === 'text-range' || refB.type === 'page-rect';
    if (!placedA || !placedB) {
      if (placedA) return -1;
      if (placedB) return 1;
      return (
        (card1.createdTimeISO || '').localeCompare(card2.createdTimeISO || '') ||
        tieBreakBySortKeyThenId(card1, card2)
      );
    }

    const docA = documentOf(refA.sourceId);
    const docB = documentOf(refB.sourceId);
    const byDocument =
      docA.rank - docB.rank || (docA.key < docB.key ? -1 : docA.key > docB.key ? 1 : 0);
    if (byDocument) return byDocument;

    if (refA.type === 'text-range' && refB.type === 'text-range') {
      return refA.start - refB.start || tieBreakBySortKeyThenId(card1, card2);
    }
    if (refA.type === 'text-range') return -1;
    if (refB.type === 'text-range') return 1;
    if (refA.type === 'page-rect' && refB.type === 'page-rect') {
      return (
        refA.pageNumber - refB.pageNumber ||
        refA.rect.y - refB.rect.y ||
        refA.rect.x - refB.rect.x ||
        tieBreakBySortKeyThenId(card1, card2)
      );
    }
    return tieBreakBySortKeyThenId(card1, card2);
  };
}

/**
 * "PDF order" when the set's document order is not at hand: the primary's
 * cards first, then each other document's, never interleaved. See
 * {@link cardSorterByDocumentPosition}.
 */
export const cardSorterByPDFPosition = cardSorterByDocumentPosition();

/**
 * `sorter` applied deck by deck. A folder's pool is several decks, and both
 * the canonical order (sortKeys) and "PDF order" (each deck's own documents,
 * a bare ref meaning THAT deck's primary) are per deck: compared across decks
 * they interleave the folder card by card. Decks keep the order they first
 * appear in `cards` (a folder pool is built deck by deck); within a deck,
 * `sorter` decides. A pool of one deck — or of cards that name none — gets
 * `sorter` itself.
 */
export function deckByDeck<CardType extends SpacedRepetitionCard>(
  cards: readonly CardType[],
  sorter: (card1: CardType, card2: CardType) => number,
): (card1: CardType, card2: CardType) => number {
  const deckOf = (card: CardType) => (card as { deckName?: unknown }).deckName;
  const rank = new Map<unknown, number>();
  for (const card of cards) {
    const deck = deckOf(card);
    if (!rank.has(deck)) rank.set(deck, rank.size);
  }
  if (rank.size < 2) return sorter;
  return (card1, card2) =>
    rank.get(deckOf(card1))! - rank.get(deckOf(card2))! || sorter(card1, card2);
}
